import React, { useState, useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import Footer from "./Footer";
import AdSlot from "./AdSlot";
import { AD_SLOTS } from "../adsConfig";
import { useTheme, monacoTheme } from "../ThemeContext";
import { load, save } from "../storage";

// localStorage keys + helpers for persisting sidebar collapse state.
const LEFT_OPEN_KEY = "ui.sidebar.left.open";
const RIGHT_OPEN_KEY = "ui.sidebar.right.open";

function loadOpen(key, fallback) {
  const v = load(key);
  return v == null ? fallback : v === "1";
}

// --- Flat, single-stroke icons (no fills, no shading) ---
function SunIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.9" y1="4.9" x2="6.3" y2="6.3" />
      <line x1="17.7" y1="17.7" x2="19.1" y2="19.1" />
      <line x1="4.9" y1="19.1" x2="6.3" y2="17.7" />
      <line x1="17.7" y1="6.3" x2="19.1" y2="4.9" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function Chevron({ dir = "left" }) {
  const d = dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6";
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

// Small ⌘+key keycap chip used in panel headers
function PanelKbd({ k }) {
  return (
    <span className="keycap-group">
      <kbd className="keycap">⌘</kbd>
      <kbd className="keycap">{k}</kbd>
    </span>
  );
}

export default function AppLayout({
  showLeftSidebar = null, // ← can be object or falsy
  showRightSidebar = true,
  structCode,
  children,
  entries,
}) {
  // Collapse state persists across visits (localStorage); defaults match the
  // original behavior — left open, right collapsed — on a fresh browser.
  const [isLeftOpen, setIsLeftOpen] = useState(() => loadOpen(LEFT_OPEN_KEY, true));
  const [isRightOpen, setIsRightOpen] = useState(() => loadOpen(RIGHT_OPEN_KEY, false));
  const [sidebarWidth, setSidebarWidth] = useState(360);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    save(LEFT_OPEN_KEY, isLeftOpen ? "1" : "0");
  }, [isLeftOpen]);
  useEffect(() => {
    save(RIGHT_OPEN_KEY, isRightOpen ? "1" : "0");
  }, [isRightOpen]);

  const { theme, toggleTheme } = useTheme();
  const sidebarRef = useRef(null);

  const hasLeft = showLeftSidebar && typeof showLeftSidebar === "object";

  // Handle right-sidebar resizing
  useEffect(() => {
    function handleMouseMove(e) {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 240 && newWidth < 720) setSidebarWidth(newWidth);
    }
    function handleMouseUp() {
      setIsResizing(false);
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // ⌘/Ctrl+B → options panel · ⌘/Ctrl+L → struct viewer
  // (⌘/Ctrl+I theme toggle lives in ThemeProvider)
  // Capture phase so we win over Monaco's editor keybindings when it's focused.
  useEffect(() => {
    function onKeyDown(e) {
      // Esc closes both sidebars (let it propagate so Monaco still handles it too)
      if (e.key === "Escape") {
        setIsLeftOpen(false);
        setIsRightOpen(false);
        return;
      }
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === "b" && hasLeft) {
        e.preventDefault();
        e.stopPropagation();
        setIsLeftOpen((v) => !v);
      } else if (key === "l" && showRightSidebar) {
        e.preventDefault();
        e.stopPropagation();
        setIsRightOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [hasLeft, showRightSidebar]);

  const appClass = [
    "app",
    isLeftOpen ? "is-open-left" : "",
    isRightOpen ? "is-open-right" : "",
  ].join(" ");

  return (
    <>
      {/* --- Top Nav --- */}
      <nav className="topnav">
        <div className="nav-left">
          <a href="/" className="logo">
            {`<${window.SITE_NAME}/>`}
          </a>

          <div className="dropdown">
            <button className="dropbtn">Formatters ▾</button>
            <div className="dropdown-content">
              {Object.entries(entries).map(([key, name]) => (
                <a key={key} href={`/formatter/${key}`}>
                  {name}
                </a>
              ))}
            </div>
          </div>

          <div className="dropdown">
            <button className="dropbtn">Converters ▾</button>
            <div className="dropdown-content">
              {Object.entries(entries).map(([key, name]) => (
                <a key={key} href={`/converter/${key}-to-xml`}>
                  {name}
                </a>
              ))}
            </div>
          </div>

          <a href="/about">About</a>
        </div>

        <div className="nav-right">
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={`Toggle theme (⌘/Ctrl+I) — currently ${theme}`}
            aria-label="Toggle color theme"
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </nav>

      <div className={appClass}>
        {/* LEFT SIDEBAR — renders only if showLeftSidebar is an object */}
        {hasLeft && (
          <aside
            className={`sidebar sidebar-left ${
              isLeftOpen ? "open" : "collapsed"
            }`}
          >
            {isLeftOpen ? (
              <div className="sidebar-content">
                <header className="panel-header">
                  <span className="panel-title">
                    {showLeftSidebar.name || "Options"}
                  </span>
                  <span className="panel-actions">
                    <PanelKbd k="B" />
                    <button
                      className="icon-btn"
                      aria-label="Collapse options panel"
                      onClick={() => setIsLeftOpen(false)}
                    >
                      <Chevron dir="left" />
                    </button>
                  </span>
                </header>
                <div className="panel-body">{showLeftSidebar.component}</div>
                <AdSlot slot={AD_SLOTS.left} />
              </div>
            ) : (
              <button
                className="rail"
                aria-label="Open options panel"
                onClick={() => setIsLeftOpen(true)}
              >
                <Chevron dir="right" />
                <span className="rail-label">Options</span>
              </button>
            )}
          </aside>
        )}

        {/* MAIN CONTENT */}
        <main>{children}</main>

        {/* RIGHT SIDEBAR (Resizable Struct View) */}
        {showRightSidebar && (
          <aside
            ref={sidebarRef}
            className={`sidebar sidebar-right ${
              isRightOpen ? "open" : "collapsed"
            }`}
            style={{
              width: isRightOpen
                ? `${sidebarWidth}px`
                : "var(--sidebar-collapsed)",
              transition: isResizing ? "none" : "width 0.25s ease",
              userSelect: isResizing ? "none" : "auto",
            }}
          >
            {isRightOpen ? (
              <div className="sidebar-content">
                <div
                  className="resize-handle"
                  onMouseDown={() => setIsResizing(true)}
                />
                <header className="panel-header">
                  <span className="panel-title">
                    <span className="dot" /> Struct View
                  </span>
                  <span className="panel-actions">
                    <PanelKbd k="L" />
                    <button
                      className="icon-btn"
                      aria-label="Collapse struct view"
                      onClick={() => setIsRightOpen(false)}
                    >
                      <Chevron dir="right" />
                    </button>
                  </span>
                </header>

                {structCode && structCode.trim() && (
                  <div className="struct-meta">
                    <span>{structSummary(structCode)}</span>
                    <button
                      className="copy-btn"
                      onClick={() =>
                        navigator.clipboard?.writeText(structCode)
                      }
                    >
                      <CopyIcon /> Copy
                    </button>
                  </div>
                )}

                <div className="struct-view">
                  {structCode && structCode.trim() ? (
                    <Editor
                      height="100%"
                      value={structCode}
                      defaultLanguage="go"
                      theme={monacoTheme(theme)}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 12,
                        lineNumbers: "off",
                        wordWrap: "off",
                        scrollBeyondLastLine: false,
                        padding: { top: 8 },
                        overviewRulerLanes: 0,
                        renderLineHighlight: "none",
                      }}
                    />
                  ) : (
                    <p className="muted">
                      No struct generated yet. Edit the input to see the
                      inferred Go structs.
                    </p>
                  )}
                </div>
                <AdSlot slot={AD_SLOTS.right} />
              </div>
            ) : (
              <button
                className="rail"
                aria-label="Open struct view"
                onClick={() => setIsRightOpen(true)}
              >
                <Chevron dir="left" />
                <span className="rail-label">Struct</span>
              </button>
            )}
          </aside>
        )}
      </div>

      <Footer entries={entries} />

      {/* Always-visible privacy banner pinned to the bottom of the viewport */}
      <div className="privacy-banner" role="note">
        <span className="privacy-icon">
          <LockIcon />
        </span>
        <p>
          <strong>formaticus never stores your data — ever.</strong> Everything
          runs locally in your browser; nothing you paste is logged, sent, or
          saved. Most other formatter sites quietly harvest what you paste —
          API keys, passwords, emails, and other secrets. We don't. We never
          have, and never will.
        </p>
      </div>
    </>
  );
}

// ---------- Utilities ----------
function CopyIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

// Summarize generated Go code: "go · N types · M fields"
function structSummary(code) {
  const types = (code.match(/^\s*type\s+\w+\s+struct\s*{/gm) || []).length;
  const fields = (code.match(/`(json|yaml):/g) || []).length;
  return `go · ${types} ${types === 1 ? "type" : "types"} · ${fields} ${
    fields === 1 ? "field" : "fields"
  }`;
}
